import { Box, CircularProgress, Popper } from "@mui/material";
import {
  SearchOutlined as SearchIcon,
  MedicationOutlined as SupplementIcon,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { v2 } from "../ui/v2Tokens";
import Thumb from "../ui/Thumb";
import { searchSupplements } from "../../../../services/SupplementService";
import type { AdminSupplement } from "../../../../api/generated/types.gen";
import { createSupplement, SUPPLEMENT_TIMINGS, type SupplementV2 } from "../model/nutritionV2Model";
import { Labeled } from "../ui/primitives";

const DEBOUNCE_MS = 250;
/**
 * A company usually has a handful of supplements, and no trainer knows their names by
 * heart — clicking the field lists them instead of demanding a keyword first.
 */
const PREVIEW_SIZE = 5;

type Props = {
  onAdd: (supplement: SupplementV2) => void;
};

const controlSx = {
  boxSizing: "border-box" as const,
  height: "36px",
  padding: "0 10px",
  borderRadius: v2.rSm,
  border: `1.5px solid ${v2.line}`,
  background: v2.surface,
  fontSize: "13px",
  fontFamily: "inherit",
  color: v2.ink,
  outline: "none",
  "&:focus": { borderColor: v2.brand },
};

/**
 * Picks a supplement from the company's real supplement list, then asks for the dosage
 * and timing. Same interaction as `FoodSearchField` — search is the only way in.
 */
const SupplementSearchField = ({ onAdd }: Props) => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<AdminSupplement[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AdminSupplement | null>(null);
  const [dosage, setDosage] = useState("");
  const [timing, setTiming] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  // `add()` refocuses the field on purpose; reopening the list there would cover the
  // supplement that was just added
  const skipFocusPreview = useRef(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const term = keyword.trim();
    if (!term || selected) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      const res = await searchSupplements({ keyword: term, page: 0, size: 12 });
      if (cancelled) return;
      const content: AdminSupplement[] = res?.data?.content || [];
      setResults(content);
      setOpen(content.length > 0);
      setIsPreview(false);
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLoading(false);
    };
  }, [keyword, selected]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !popperRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  /** Clicking into an empty field lists the company's supplements rather than nothing. */
  const showPreview = async () => {
    setLoading(true);
    const res = await searchSupplements({ page: 0, size: PREVIEW_SIZE });
    const content: AdminSupplement[] = res?.data?.content || [];
    setResults(content);
    setIsPreview(true);
    setOpen(content.length > 0);
    setLoading(false);
  };

  const onFocus = () => {
    if (selected) return;
    if (skipFocusPreview.current) {
      skipFocusPreview.current = false;
      return;
    }
    if (results.length > 0) {
      setOpen(true);
      return;
    }
    if (!keyword.trim()) showPreview();
  };

  const reset = () => {
    setSelected(null);
    setKeyword("");
    setDosage("");
    setTiming("");
    setResults([]);
    setIsPreview(false);
  };

  const add = () => {
    if (!selected) return;
    onAdd(
      createSupplement({
        supplementId: selected.id,
        name: selected.name || "",
        imageUrl: selected.imageUrl,
        purchaseLink: selected.purchaseLink,
        dosage,
        timing,
      })
    );
    reset();
    skipFocusPreview.current = true;
    inputRef.current?.focus();
  };

  return (
    <Box
      ref={wrapRef}
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: "10px",
        flexWrap: "wrap",
        padding: "10px 12px",
        borderTop: `1px dashed ${v2.line}`,
        background: v2.canvas,
      }}
    >
      <Box ref={fieldRef} sx={{ position: "relative", flex: "1 1 180px", minWidth: "150px" }}>
        <Box
          component="input"
          ref={inputRef}
          type="text"
          autoComplete="off"
          placeholder="חפש תוסף תזונה..."
          aria-label="חפש תוסף תזונה..."
          value={keyword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setKeyword(e.target.value);
            if (selected) setSelected(null);
          }}
          onFocus={onFocus}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && selected) add();
            if (e.key === "Escape") reset();
          }}
          sx={{
            ...controlSx,
            width: "100%",
            paddingInlineStart: "32px",
            borderColor: selected ? v2.brand : v2.line,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            insetInlineStart: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            color: v2.ink3,
            pointerEvents: "none",
          }}
        >
          {loading ? <CircularProgress size={12} sx={{ color: v2.ink3 }} /> : <SearchIcon sx={{ fontSize: 15 }} />}
        </Box>

        <Popper
          open={open}
          anchorEl={fieldRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: fieldRef.current?.offsetWidth, maxWidth: 420 }}
        >
          <Box
            ref={popperRef}
            sx={{
              marginTop: "4px",
              maxHeight: "230px",
              overflowY: "auto",
              background: v2.surface,
              border: `1.5px solid ${v2.line}`,
              borderRadius: v2.rSm,
              boxShadow: v2.shadowMenu,
            }}
          >
            {isPreview && (
              <Box
                sx={{
                  padding: "6px 10px",
                  fontSize: "10.5px",
                  fontWeight: 700,
                  color: v2.ink3,
                  background: v2.canvas,
                  borderBottom: `1px solid ${v2.lineSoft}`,
                }}
              >
                התוספים של החברה — הקלד כדי לחפש
              </Box>
            )}
            {results.map((supplement) => (
              <Box
                key={supplement.id}
                component="button"
                type="button"
                data-testid="supplement-search-option"
                onClick={() => {
                  setSelected(supplement);
                  setKeyword(supplement.name || "");
                  setOpen(false);
                }}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "start",
                  padding: "7px 10px",
                  border: "none",
                  borderBottom: `1px solid ${v2.lineSoft}`,
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { background: v2.canvas },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Thumb src={supplement.imageUrl} size={30} icon={SupplementIcon} />
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: v2.ink }}>
                      {supplement.name}
                    </Box>
                    {supplement.description && (
                      <Box
                        sx={{
                          fontSize: "10.5px",
                          color: v2.ink3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {supplement.description}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Popper>
      </Box>

      <Labeled label="מינון">
        <Box
          component="input"
          type="text"
          aria-label="מינון"
          placeholder="2 כפות"
          value={dosage}
          disabled={!selected}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDosage(e.target.value)}
          sx={{ ...controlSx, width: "110px", background: selected ? v2.surface : v2.canvas }}
        />
      </Labeled>

      <Labeled label="תזמון">
        <Box
          component="input"
          type="text"
          aria-label="תזמון"
          placeholder="לפני אימון"
          value={timing}
          disabled={!selected}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTiming(e.target.value)}
          sx={{ ...controlSx, width: "130px", background: selected ? v2.surface : v2.canvas }}
        />
      </Labeled>

      <Box
        component="button"
        type="button"
        onClick={add}
        disabled={!selected}
        sx={{
          height: "36px",
          padding: "0 16px",
          borderRadius: v2.rSm,
          border: "none",
          background: selected ? `linear-gradient(135deg, ${v2.brand}, ${v2.brandDark})` : v2.line,
          color: selected ? "#fff" : v2.ink3,
          fontSize: "12.5px",
          fontWeight: 700,
          fontFamily: "inherit",
          cursor: selected ? "pointer" : "not-allowed",
          whiteSpace: "nowrap",
        }}
      >
        + הוסף
      </Box>

      {/* quick timings — the field stays free text, these just fill it in */}
      {selected && (
        <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap", width: "100%" }}>
          {SUPPLEMENT_TIMINGS.map((option) => (
            <Box
              key={option}
              component="button"
              type="button"
              onClick={() => setTiming(option)}
              sx={{
                padding: "3px 10px",
                borderRadius: "20px",
                border: `1px solid ${timing === option ? v2.brand : v2.line}`,
                background: timing === option ? v2.brandWash : v2.surface,
                color: timing === option ? v2.brandText : v2.ink2,
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              {option}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SupplementSearchField;
